import type { CSSProperties, FC } from "react";
import type { ContentFileDto } from "shared";
import { ContentType } from "shared";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "./CreativeMediaPreview.css";

function getPosterUrl(file: ContentFileDto): string | undefined {
	return file.thumbnails["256"] ?? file.thumbnails["512"];
}

const MediaItem: FC<{
	file: ContentFileDto;
	style?: CSSProperties;
	showControls?: boolean;
}> = ({ file, style, showControls = true }) => {
	if (file.type === ContentType.VIDEO) {
		return (
			<video
				controls={showControls}
				muted
				preload="metadata"
				poster={getPosterUrl(file)}
				style={style}
			>
				<source src={file.url} type={`video/${file.extension}`} />
			</video>
		);
	}

	return (
		<img
			src={getPosterUrl(file) ?? file.url}
			alt={file.filename}
			style={style}
		/>
	);
};

const THUMB_STYLE: CSSProperties = {
	maxWidth: 300,
	maxHeight: 200,
	borderRadius: 8,
	border: "1px solid #dee2e6",
	objectFit: "cover",
	display: "block",
};

const CAROUSEL_MEDIA_STYLE: CSSProperties = {
	width: "100%",
	height: "100%",
	display: "block",
	objectFit: "cover",
};

export const CreativeMediaPreview: FC<{
	files: ContentFileDto[];
	variant?: "thumb" | "full";
	showControls?: boolean;
	fill?: boolean;
	/** Swipeable carousel (Meta carousel ads). */
	layout?: "grid" | "carousel";
	/** Used with layout="carousel" — defaults to 1:1 for feed, pass 9/16 for stories. */
	aspectRatio?: string;
}> = ({
	files,
	variant = "thumb",
	showControls = true,
	fill = false,
	layout = "grid",
	aspectRatio = "1 / 1",
}) => {
	if (files.length === 0) {
		return null;
	}

	const fullStyle: CSSProperties =
		variant === "full"
			? {
					width: "100%",
					...(fill ? { height: "100%" } : {}),
					display: "block",
					objectFit: "cover",
				}
			: THUMB_STYLE;

	if (layout === "carousel") {
		const containerStyle: CSSProperties = {
			width: "100%",
			overflow: "hidden",
			background: "#f0f2f5",
			...(fill ? { height: "100%" } : { aspectRatio }),
		};

		if (files.length === 1) {
			return (
				<div style={containerStyle}>
					<MediaItem
						file={files[0]}
						style={CAROUSEL_MEDIA_STYLE}
						showControls={showControls}
					/>
				</div>
			);
		}

		return (
			<div style={containerStyle} className="meta-ads-carousel-preview">
				<Swiper
					modules={[Pagination]}
					slidesPerView={1}
					pagination={{ clickable: true }}
					style={{ width: "100%", height: "100%" }}
				>
					{files.map((file) => (
						<SwiperSlide
							key={file.id}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
							}}
						>
							<MediaItem
								file={file}
								style={CAROUSEL_MEDIA_STYLE}
								showControls={showControls}
							/>
						</SwiperSlide>
					))}
				</Swiper>
			</div>
		);
	}

	if (files.length === 1) {
		return (
			<MediaItem
				file={files[0]}
				style={fullStyle}
				showControls={showControls}
			/>
		);
	}

	return (
		<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
			{files.map((file) => (
				<MediaItem
					key={file.id}
					file={file}
					style={fullStyle}
					showControls={showControls}
				/>
			))}
		</div>
	);
};
